import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline';
import { Button } from '@monorepo/shared/componentsV2/Button';
import { getFieldNameWithPrefix } from '@monorepo/shared/componentsV2/fields/fieldUtils';
import { ErrorText } from '@monorepo/shared/componentsV2/Input';
import { useMoveFocus } from '@monorepo/shared/hooks/useMoveFocus';
import { hasUniqueNonEmptyValues } from '@monorepo/shared/utils/validators';
import {
  FormConditionalOperator,
  FormTemplateFieldConditionalRule,
  FormTemplateFieldTypes,
  OPERATORS_THAT_REQUIRE_FIELD_VALUE,
} from 'mapistry-shared';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useField, useForm } from 'react-final-form';
import { FieldArray, useFieldArray } from 'react-final-form-arrays';
import styled from 'styled-components';
import { EditableFormTemplateField } from '../../types';
import { ConditionalFieldValueInputRow } from './ConditionalFieldValueInputRow';

const FieldConditionValuesList = styled.div`
  display: flex;
  flex-direction: column;
  width: 80%;
`;

const AddButtonContainer = styled.span`
  margin-top: 0.2rem;
`;

function isFieldValueNeeded(operator: FormConditionalOperator): boolean {
  return OPERATORS_THAT_REQUIRE_FIELD_VALUE.has(operator);
}

function getCanHaveMultipleValues(
  triggerField: EditableFormTemplateField,
): boolean {
  return triggerField.type !== FormTemplateFieldTypes.BOOLEAN;
}

function useHandleOperatorChange(prefix: string) {
  const { change } = useForm();
  const operatorFieldName = getFieldNameWithPrefix('operator', prefix);

  const {
    input: { value: operator },
  } = useField(operatorFieldName);

  const conditionalFieldValuesFieldName = getFieldNameWithPrefix(
    'conditionalFieldValues',
    prefix,
  );

  const {
    input: { value: conditionalFieldValues },
  } = useField(conditionalFieldValuesFieldName);

  useEffect(() => {
    if (!isFieldValueNeeded(operator)) {
      change(conditionalFieldValuesFieldName, null);
    } else if (!conditionalFieldValues) {
      change(conditionalFieldValuesFieldName, ['']);
    }
  }, [
    change,
    conditionalFieldValues,
    conditionalFieldValuesFieldName,
    operator,
    prefix,
  ]);
}

interface ConditionalFieldValuesPickerProps {
  rulePrefix: string;
  rule: FormTemplateFieldConditionalRule;
  triggerField: EditableFormTemplateField;
}

export function ConditionalFieldValuesPicker({
  rulePrefix,
  rule,
  triggerField,
}: ConditionalFieldValuesPickerProps) {
  useHandleOperatorChange(rulePrefix);

  const conditionalFieldValuesFieldName = getFieldNameWithPrefix(
    'conditionalFieldValues',
    rulePrefix,
  );

  const {
    fields: {
      value: conditionalFieldValues,
      remove: removeConditionalFieldValue,
      push: addConditionalFieldValue,
    },
    meta,
  } = useFieldArray<string | undefined>(conditionalFieldValuesFieldName, {
    validate: hasUniqueNonEmptyValues,
  });

  const handleAdd = useCallback(() => {
    addConditionalFieldValue('');
  }, [addConditionalFieldValue]);

  const { moveFocus, focusCatcherRef } = useMoveFocus();
  const onDelete = useCallback(
    (index: number) => {
      const isLastFieldValue = index === conditionalFieldValues.length - 1;
      const willDeleteButtonDisappear = conditionalFieldValues.length <= 2;
      const shouldMoveFocusAfterDelete =
        willDeleteButtonDisappear || isLastFieldValue;

      removeConditionalFieldValue(index);
      if (shouldMoveFocusAfterDelete) {
        moveFocus();
      }
    },
    [conditionalFieldValues.length, removeConditionalFieldValue, moveFocus],
  );

  const canHaveMultipleValues = useMemo(
    () => getCanHaveMultipleValues(triggerField),
    [triggerField],
  );

  if (!isFieldValueNeeded(rule.operator)) {
    return null;
  }

  // If an individual field has an error, the meta error will be an array
  // If it's an overall error (e.g. for non-unique values) it will be a string
  // Each field can show its own error, only show overall errors here
  const hasOverallError = !!meta.error && typeof meta.error === 'string';

  return (
    <FieldConditionValuesList>
      <FieldArray name={conditionalFieldValuesFieldName}>
        {({ fields }) => (
          <>
            {fields.map((name: string, index: number) => (
              <ConditionalFieldValueInputRow
                key={name}
                name={name}
                hasOverallError={hasOverallError}
                index={index}
                rulePrefix={rulePrefix}
                triggerField={triggerField}
                onDelete={onDelete}
                isDeletable={conditionalFieldValues.length > 1}
              />
            ))}
          </>
        )}
      </FieldArray>
      {hasOverallError && <ErrorText>{meta.error}</ErrorText>}
      {canHaveMultipleValues && (
        <AddButtonContainer>
          <Button color="primary" onClick={handleAdd} ref={focusCatcherRef}>
            <AddCircleOutlineIcon aria-hidden />
            Add another condition
          </Button>
        </AddButtonContainer>
      )}
    </FieldConditionValuesList>
  );
}
